<script setup lang="ts">
import { AanvragenDomainEntitiesOntvangerDirectePost as OntvangerDirectePost } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesOntvangerDirectePost';
import { type AanvragenMuterenStartMutatieMutatieDto as StartMutatieDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenStartMutatieMutatieDto';
import { useMuterenStart } from '@services/aanvragen-public/orval-api/muteren-api/muteren-api';
import VModal from '@shared/component/VModal/VModal.vue';
import { toDateonly } from '@tools/dates';
import { computed, nextTick, ref, watch } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useField, useForm } from 'vee-validate';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import { useErrorHandler } from '@errors/useErrorHandler';
import { usePakketpolisStore } from '@crm-lib/Polissen';
import { type MutatieGestart } from '../PakketpolisToolbar.vue';

const props = defineProps<{
  pakketpolisnummer: string;
}>();

const emit = defineEmits<{
  mutatieGestart: [MutatieGestart];
}>();

const { handleError } = useErrorHandler();

const store = usePakketpolisStore();

const isOpen = defineModel<boolean>('isOpen');
const mutatiedatum = ref<Date>(new Date());
const latenUitvallen = ref<boolean>(true);
const extraOpmerkingenField = ref<InstanceType<typeof TextField>>();

const pakketpolis = computed(() => store.getPakketpolis(props.pakketpolisnummer));

const validationSchema = toTypedSchema(
  z.object({
    extraOpmerkingen: z.string().min(1, 'Extra opmerking is verplicht.'),
  }),
);

const { errors, handleSubmit, isSubmitting } = useForm({
  validationSchema,
});

const { value: extraOpmerkingen } = useField<string>('extraOpmerkingen');

const { mutateAsync: startMutatie } = useMuterenStart({
  request: {
    timeout: 2 * 60 * 1000,
  },
});

const onSubmit = handleSubmit(async () => {
  if (!pakketpolis.value?.mutatieActies.muteren.isMogelijk) return;

  const dto: StartMutatieDto = {
    isDummy: true,
    mutatiedatum: toDateonly(mutatiedatum.value),
    forceerUitval: latenUitvallen.value,
    hoofdvervaldatum: pakketpolis.value.originelePakketpolis.hoofdvervaldatum,
    incassocode: pakketpolis.value.originelePakketpolis.incasso.code,
    termijn: pakketpolis.value.originelePakketpolis.termijn.termijn,
    nieuweProducten: [],
    teMuterenPolissen: [],
    ontvangerDirectePost: OntvangerDirectePost.Adviseur,
    extraOpmerkingen: extraOpmerkingen.value,
    isBeursPakket: true,
  };
  try {
    const res = await startMutatie({ polisnummerpakket: props.pakketpolisnummer, data: dto });
    emit('mutatieGestart', {
      directTeVerwerken: res.directTeVerwerken,
      offertenummer: res.offertenummer,
      offerteproductId: res.offerteproductId,
      isBeursPakket: true,
    });
  } catch (e: any) {
    handleError(e);
  }
});

watch(isOpen, () => {
  if (isOpen.value) {
    nextTick(() => extraOpmerkingenField.value?.focus());
  }
});

watch(
  () => pakketpolis.value?.extraOpmerkingen,
  (eo) => {
    if (eo) {
      extraOpmerkingen.value = eo ?? '';
    }
  },
  { immediate: true },
);

function close() {
  isOpen.value = false;
}
</script>

<template>
  <VModal
    v-if="pakketpolis"
    :is-active="isOpen"
    @close-modal="close"
    @confirm-action="onSubmit"
    title="Mutatie starten"
    confirm-button-text="Mutatie starten"
    :overflow="true"
    :invert-header="true"
    :loading="isSubmitting"
  >
    <FieldRowGroup :gap="2">
      <FieldRowV2
        class="mb-2"
        label="Mutatiedatum"
        :tooltip="pakketpolis.mutatieActies.dummymutatieStarten?.melding ?? undefined"
        :label-on-own-row="true"
        label-text-color="primary900"
      >
        <VDatePicker
          v-model="mutatiedatum"
          :disabled="!pakketpolis.mutatieActies.dummymutatieStarten?.isMogelijk"
          :clearable="false"
          data-testid="dummy-ingangsdatum"
        />
      </FieldRowV2>
      <FieldRowV2
        label="Mutatie toelichting"
        :label-on-own-row="true"
        label-text-color="primary900"
        :errors="[errors.extraOpmerkingen]"
        tooltip="Het is niet mogelijk voor open broked polissen om zelf de gegevens te wijzigen. Geef in het onderstaand veld de gewenste wijzigingen door."
      >
        <TextField
          v-model:value="extraOpmerkingen"
          :rows="4"
          placeholder="Geef hier de gewenste wijzigingen door."
          ref="extraOpmerkingenField"
        />
      </FieldRowV2>
    </FieldRowGroup>
  </VModal>
</template>
